import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Globals } from '../../globals';
import { UpdateGroupParams } from '../../interfaces';
import { Functionblock } from '../functionblocks/functionblocks.interfaces';
import { Group, GroupType, IDashboardFilter } from './groups.interfaces';

@Injectable({
  providedIn: 'root',
})
/**
 * Groups REST calls
 */
export class GroupsService {
  private readonly http = inject(HttpClient);
  private readonly globals = inject(Globals);

  /**
   * Get all groups
   */
  getGroups(): Observable<Group[]> {
    const url = `${this.globals.apiRoot}/jwtgroups`;
    return this.http.get<Group[]>(url);
  }

  /**
   * Get all groups by function block id
   */
  getGroupsByFunctionBlockId(functionBlockId: number): Observable<Group[]> {
    const url = `${this.globals.apiRoot}/jwtfunctionblocks/${functionBlockId}/groups`;
    return this.http.get<Group[]>(url);
  }

  /**
   * Get energy group.
   * @returns Energy group if exists, else null.
   */
  getEnergyGroup(): Observable<Group | null> {
    const url = `${this.globals.apiRoot}/jwtgroups/energy`;
    return this.http.get<Group | null>(url);
  }

  /**
   * Get all groups and shared groups
   */
  getGroupsAndSharedGroups(): Observable<Group[]> {
    const url = `${this.globals.apiRoot}/jwtgroups/shared`;
    return this.http.get<Group[]>(url);
  }

  /**
   * Get all groups and their functionblocks
   */
  getGroupsWithFunctionBlocks(): Observable<Group[]> {
    const url = `${this.globals.apiRoot}/jwtgroups/functionblocks`;
    return this.http.get<Group[]>(url);
  }

  /**
   * Check whether a user has an energy group
   * @returns
   */
  doEnergyGroupsExist(): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtgroups/doEnergyGroupsExist`;
    return this.http.get<boolean>(url);
  }

  /**
   * Get the type of a group.
   * @param id
   * @returns group type
   */
  getType(id: number): Observable<GroupType> {
    const url = `${this.globals.apiRoot}/jwtgroups/${id}/type`;
    return this.http.get<GroupType>(url);
  }

  /**
   * gets the filter to start the communication of the app
   * @returns
   */
  getDashboardFilter(): Observable<IDashboardFilter> {
    const url = `${this.globals.apiRoot}/jwtgroups/filter`;
    return this.http.get<IDashboardFilter>(url);
  }

  /**
   * Update the order of the groups
   * @param groups groups in new order
   */
  updateGroupsOrder(groups: Group[]): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtgroups/order`;
    return this.http.put<void>(url, groups);
  }

  /**
   * Update a group
   * @param group the group to update
   */
  updateGroup(group: Group): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtgroups/${group.id}`;
    return this.http.put<void>(url, group);
  }

  updateGroupV2(group: UpdateGroupParams): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtgroups/v2/${group.group.id}`;
    return this.http.put<void>(url, group);
  }

  /**
   * Delete a group
   * @param id the group id
   */
  deleteGroup(id: number): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtgroups/${id}`;
    return this.http.delete<boolean>(url);
  }

  /**
   * Get all device details and functionblocks for by device id
   * @param id device id
   */
  getGroupWithFunctionBlocks(
    id: number,
    addDevices = false,
  ): Observable<Group> {
    const url = `${this.globals.apiRoot}/jwtgroups/${id}/functionblocks?addDevices=${addDevices}`;
    return this.http.get<Group>(url);
  }

  /**
   * Add functionblock to group
   * @param id group id
   * @param fbId id of the functionblock to add
   */
  addFunctionblockToGroup(id: number, fbId: number): Observable<Group> {
    const url = `${this.globals.apiRoot}/jwtgroups/${id}/functionblocks/${fbId}`;
    return this.http.post<Group>(url, fbId);
  }

  /**
   * Delete functionblock from group
   * @param id group id
   * @param fbId id of the functionblock to delete
   */
  deleteFunctionblockFromGroup(id: number, fbId: number): Observable<Group> {
    const url = `${this.globals.apiRoot}/jwtgroups/${id}/functionblocks/${fbId}`;
    return this.http.delete<Group>(url);
  }

  /**
   * Update the order of the functionblocks in a group
   * @param id group id to update the fb order in
   * @param functionblocks functionblocks in new order
   */
  updateFunctionblockOrder(
    id: number,
    functionblocks: Functionblock[],
  ): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtgroups/${id}/functionblocks/order`;
    return this.http.put<void>(url, functionblocks);
  }

  uploadBackGroundImage(id: number, formData: FormData): Observable<string> {
    const url = `${this.globals.apiRoot}/jwtgroups/${id}/background`;
    return this.http.put<string>(url, formData);
  }

  /**
   * Delete the background image of a group
   * @param id group id
   */
  deleteBackGroundImage(id: number): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtgroups/${id}/background`;
    return this.http.delete<void>(url);
  }
}
